import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import { X } from 'lucide-react';
import {
  coerceData,
  getNumericKeys,
  resolveKey,
  resolveBarKeys,
} from '../chartUtils';
import type { ChartConfig, ChartSpec } from '../chartUtils';
import { buildStandaloneHtml } from '../chartExport';
import ChartError from './ChartError';
import { normalizeSpec, safeFilename } from './normalizeSpec';
import BarChartView from './BarChartView';
import LineChartView from './LineChartView';
import PieChartView from './PieChartView';
import MapView from './MapView';

export type { ChartSpec } from '../chartUtils';

// ─── Main Component ──────────────────────────────────────────────────

function ChartRendererInner({ spec: rawSpec }: { spec: ChartSpec }) {
  const [expanded, setExpanded] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);
  const fullscreenChartRef = useRef<HTMLDivElement>(null);

  // Normalize: handle flat specs where config fields are at root level
  const spec = useMemo(() => normalizeSpec(rawSpec as unknown as Record<string, unknown>), [rawSpec]);

  // Coerce data: convert string numbers to actual numbers (hooks must be before returns)
  const hasData = !!(spec && spec.data && spec.data.length > 0 && spec.config);
  const numericKeys = useMemo(
    () => (hasData ? getNumericKeys(spec.data) : new Set<string>()),
    [hasData, spec?.data],
  );
  const data = useMemo(() => (hasData ? coerceData(spec.data, numericKeys) : []), [hasData, spec?.data, numericKeys]);

  const config = useMemo(() => (hasData ? spec.config : ({} as ChartConfig)), [hasData, spec?.config]);

  // Validate after coercion
  const validationError = useMemo(() => {
    if (!hasData || data.length === 0) return null;
    if (spec.chart_type === 'bar' || spec.chart_type === 'horizontal_bar') {
      const { valueKey } = resolveBarKeys(config, data);
      const hasNumeric = data.some((d) => typeof d[valueKey] === 'number' && d[valueKey] != null);
      if (!hasNumeric) return `No numeric values for "${valueKey}".`;
    }
    if (spec.chart_type === 'line') {
      const yKey = resolveKey(config, 'y', data);
      const hasNumeric = data.some((d) => typeof d[yKey] === 'number' && d[yKey] != null);
      if (!hasNumeric) return `No numeric values for "${yKey}".`;
    }
    if (spec.chart_type === 'pie') {
      const vKey = resolveKey(config, 'value', data);
      const hasNumeric = data.some((d) => typeof d[vKey] === 'number' && d[vKey] != null);
      if (!hasNumeric) return `No numeric values for "${vKey}".`;
    }
    return null;
  }, [hasData, spec?.chart_type, config, data]);

  const filename = useMemo(() => (hasData ? safeFilename(spec.title) : 'chart'), [hasData, spec?.title]);

  const downloadPng = useCallback(async (target: HTMLDivElement | null) => {
    if (!target) return;
    try {
      const dataUrl = await toPng(target, {
        backgroundColor: '#1a1a1a',
        pixelRatio: 2,
      });
      const link = document.createElement('a');
      link.download = `${filename}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('PNG export failed:', err);
    }
  }, [filename]);

  const downloadHtml = useCallback(() => {
    if (!hasData) return;
    const html = buildStandaloneHtml({ ...spec, data }, config);
    const blob = new Blob([html], { type: 'text/html' });
    const link = document.createElement('a');
    link.download = `${filename}.html`;
    link.href = URL.createObjectURL(blob);
    link.click();
    URL.revokeObjectURL(link.href);
  }, [hasData, spec, data, config, filename]);

  if (!hasData) return null;

  if (validationError) {
    return <ChartError message={validationError} spec={{ ...spec, data }} />;
  }

  const renderChart = (fullscreen = false) => {
    const cfg = fullscreen ? { ...config, height: undefined } : config;
    switch (spec.chart_type) {
      case 'bar':
      case 'horizontal_bar':
        return <BarChartView data={data} config={cfg} isHorizontal={spec.chart_type === 'horizontal_bar'} fullscreen={fullscreen} />;
      case 'line':
        return <LineChartView data={data} config={cfg} fullscreen={fullscreen} />;
      case 'pie':
        return <PieChartView data={data} config={cfg} fullscreen={fullscreen} />;
      case 'map':
        return <MapView data={data} config={cfg} fullscreen={fullscreen} />;
      default:
        return null;
    }
  };

  const isMap = spec.chart_type === 'map';

  const DownloadButtons = ({ onPng }: { onPng: () => void }) => (
    <div className="flex items-center gap-1">
      {!isMap && (
        <button
          onClick={onPng}
          className="text-[11px] text-muted-foreground/50 hover:text-primary transition-colors cursor-pointer px-1.5 py-0.5 rounded"
          title="Download as PNG image"
        >
          PNG
        </button>
      )}
      <button
        onClick={downloadHtml}
        className="text-[11px] text-muted-foreground/50 hover:text-primary transition-colors cursor-pointer px-1.5 py-0.5 rounded"
        title="Download as interactive HTML"
      >
        HTML
      </button>
    </div>
  );

  return (
    <>
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1">
          <div>
            <div className="text-sm font-medium text-foreground/80">{spec.title}</div>
            {spec.subtitle && <div className="text-xs text-muted-foreground/60">{spec.subtitle}</div>}
            {config.total_annotation && <div className="text-xs text-primary/70 font-medium">{config.total_annotation}</div>}
          </div>
          <div className="flex items-center gap-2">
            <DownloadButtons onPng={() => downloadPng(chartRef.current)} />
            <button
              onClick={() => setExpanded(true)}
              className="text-[11px] text-muted-foreground/50 hover:text-primary transition-colors cursor-pointer flex items-center gap-1"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"
                />
              </svg>
              Expand
            </button>
          </div>
        </div>
        <div ref={chartRef}>
          {renderChart()}
        </div>
      </div>

      {expanded && (
        <div
          className="fixed inset-0 z-[999] bg-black/90 backdrop-blur-sm flex flex-col"
          onClick={(e) => { if (e.target === e.currentTarget) setExpanded(false); }}
        >
          <div className="flex items-center justify-between px-6 py-3 bg-card border-b border-primary/20">
            <div>
              <span className="text-sm font-medium text-foreground">{spec.title}</span>
              {spec.subtitle && <span className="text-xs text-muted-foreground/60 ml-3">{spec.subtitle}</span>}
            </div>
            <div className="flex items-center gap-3">
              <DownloadButtons onPng={() => downloadPng(fullscreenChartRef.current)} />
              <button
                onClick={() => setExpanded(false)}
                className="p-2.5 rounded-lg bg-primary/20 text-foreground hover:bg-primary/30 transition-colors"
                aria-label="Close fullscreen"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 p-6 flex items-center justify-center" onClick={(e) => { if (e.target === e.currentTarget) setExpanded(false); }}>
            <div ref={fullscreenChartRef} className="w-full max-w-6xl h-full">
              {renderChart(true)}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Wrapper that fetches remote data from data_url before rendering. */
function ChartRendererWithDataFetch({ spec }: { spec: ChartSpec }) {
  const [remoteData, setRemoteData] = useState<Record<string, unknown>[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedUrl = useRef<string | null>(null);

  useEffect(() => {
    if (!spec.data_url || fetchedUrl.current === spec.data_url) return;
    fetchedUrl.current = spec.data_url;
    setLoading(true);
    setError(null);
    fetch(spec.data_url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setRemoteData(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch chart data:', err);
        setError(`Failed to load chart data: ${err.message}`);
        setLoading(false);
      });
  }, [spec.data_url]);

  if (spec.data_url && loading) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full mr-3" />
        Loading {spec.total_data_points?.toLocaleString() ?? ''} data points...
      </div>
    );
  }

  if (spec.data_url && error) {
    return (
      <div className="p-4">
        <div className="text-red-400 text-sm mb-2">{error}</div>
        <div className="text-muted-foreground text-xs mb-3">
          Showing {spec.data.length} of {spec.total_data_points?.toLocaleString() ?? '?'} points (partial preview).
        </div>
        {spec.data.length > 0 && <ChartRendererInner spec={spec} />}
      </div>
    );
  }

  // If remote data was fetched, merge it into the spec
  const resolvedSpec = remoteData ? { ...spec, data: remoteData } : spec;
  return <ChartRendererInner spec={resolvedSpec} />;
}

export default memo(ChartRendererWithDataFetch, (prev, next) => {
  if (prev.spec === next.spec) return true;
  try {
    return JSON.stringify(prev.spec) === JSON.stringify(next.spec);
  } catch {
    return false;
  }
});
